import type {
  CreditCard,
  MoneyIn,
  MoneyOut,
  MonthOverview,
  MonthOverviewDetails,
  MonthlyOverview,
  Paragraph1,
  Paragraph2,
  Savings,
} from "./contentItemSchemas";
import { ContentSectionType } from "@/types/contentSectionType";
import {
  CREDIT_CARD_KEYS,
  MONEY_IN_KEYS,
  MONEY_OUT_KEYS,
  MONTHLY_OVERVIEW_RECORD_KEYS,
  MONTH_OVERVIEW_DETAILS_PARSED_KEYS,
  PARAGRAPH1_KEYS,
  PARAGRAPH2_KEYS,
} from "@/constants/monthOverviewKeys";
import { isRecord } from "./shared";
import { parseOptionalTextContent } from "./text";
import {
  monthOverviewDetailsFieldsSchema,
  monthOverviewFieldsSchema,
  monthlyOverviewFieldsSchema,
  parseMonthOverviewFromFields,
  parseWithRecord,
} from "./schemas";

export const parseMonthOverview = (
  item: unknown,
  formLabel?: string,
): MonthOverview => {
  const parsed = parseWithRecord(item, monthOverviewFieldsSchema);
  return parseMonthOverviewFromFields(parsed, formLabel);
};

const parseParagraph1 = (item: unknown): Paragraph1 => {
  const record = isRecord(item) ? item : {};
  return {
    default: parseOptionalTextContent(record[PARAGRAPH1_KEYS.DEFAULT]),
    positiveWithSavings: parseOptionalTextContent(
      record[PARAGRAPH1_KEYS.POSITIVE_WITH_SAVINGS],
    ),
    positiveWithPerfectSavings: parseOptionalTextContent(
      record[PARAGRAPH1_KEYS.POSITIVE_WITH_PERFECT_SAVINGS],
    ),
    positiveWithNegativeSavings: parseOptionalTextContent(
      record[PARAGRAPH1_KEYS.POSITIVE_WITH_NEGATIVE_SAVINGS],
    ),
    positiveWithNoSavings: parseOptionalTextContent(
      record[PARAGRAPH1_KEYS.POSITIVE_WITH_NO_SAVINGS],
    ),
    negativeWthNegativeSavings: parseOptionalTextContent(
      record[PARAGRAPH1_KEYS.NEGATIVE_WTH_NEGATIVE_SAVINGS],
    ),
  };
};

const parseParagraph2 = (item: unknown): Paragraph2 => {
  const record = isRecord(item) ? item : {};
  return {
    loanAndCreditCard: parseOptionalTextContent(
      record[PARAGRAPH2_KEYS.LOAN_AND_CREDIT_CARD],
    ),
    onlyCreditCard: parseOptionalTextContent(
      record[PARAGRAPH2_KEYS.ONLY_CREDIT_CARD],
    ),
    onlyLoan: parseOptionalTextContent(record[PARAGRAPH2_KEYS.ONLY_LOAN]),
  };
};

export const parseMonthOverviewDetails = (
  item: unknown,
  formLabel?: string,
): MonthOverviewDetails => {
  const parsed = parseWithRecord(item, monthOverviewDetailsFieldsSchema);
  return {
    type: ContentSectionType.MonthOverviewDetails,
    ...parsed,
    formLabel: formLabel ?? parsed.formLabel,
    paragraph1: parseParagraph1(
      parsed[MONTH_OVERVIEW_DETAILS_PARSED_KEYS.PARAGRAPH1] ?? {},
    ),
    paragraph2: parseParagraph2(
      parsed[MONTH_OVERVIEW_DETAILS_PARSED_KEYS.PARAGRAPH2] ?? {},
    ),
    paragraph3: parseOptionalTextContent(
      parsed[MONTH_OVERVIEW_DETAILS_PARSED_KEYS.PARAGRAPH3],
    ),
  };
};

const parseMoneyIn = (item: unknown): MoneyIn => {
  const record = isRecord(item) ? item : {};
  return {
    other: parseOptionalTextContent(record[MONEY_IN_KEYS.OTHER]),
    loan: parseOptionalTextContent(record[MONEY_IN_KEYS.LOAN]),
    otherAndLoan: parseOptionalTextContent(
      record[MONEY_IN_KEYS.OTHER_AND_LOAN],
    ),
    zero: parseOptionalTextContent(record[MONEY_IN_KEYS.ZERO]),
  };
};

const parseMoneyOut = (item: unknown): MoneyOut => {
  const record = isRecord(item) ? item : {};
  return {
    other: parseOptionalTextContent(record[MONEY_OUT_KEYS.OTHER]),
    creditCard: parseOptionalTextContent(record[MONEY_OUT_KEYS.CREDIT_CARD]),
    loan: parseOptionalTextContent(record[MONEY_OUT_KEYS.LOAN]),
    otherAndLoan: parseOptionalTextContent(
      record[MONEY_OUT_KEYS.OTHER_AND_LOAN],
    ),
    otherAndCreditCard: parseOptionalTextContent(
      record[MONEY_OUT_KEYS.OTHER_AND_CREDIT_CARD],
    ),
    loanAndCreditCard: parseOptionalTextContent(
      record[MONEY_OUT_KEYS.LOAN_AND_CREDIT_CARD],
    ),
    otherAndLoanAndCreditCard: parseOptionalTextContent(
      record[MONEY_OUT_KEYS.OTHER_AND_LOAN_AND_CREDIT_CARD],
    ),
    zero: parseOptionalTextContent(record[MONEY_OUT_KEYS.ZERO]),
  };
};

const parseCreditCard = (item: unknown): CreditCard => {
  const record = isRecord(item) ? item : {};
  return {
    negativeTransactions: parseOptionalTextContent(
      record[CREDIT_CARD_KEYS.NEGATIVE_TRANSACTIONS],
    ),
    payments: parseOptionalTextContent(record[CREDIT_CARD_KEYS.PAYMENTS]),
    paymentsAndNegativeTransactions: parseOptionalTextContent(
      record[CREDIT_CARD_KEYS.PAYMENTS_AND_NEGATIVE_TRANSACTIONS],
    ),
  };
};

const SAVINGS_KEYS: (keyof Savings)[] = [
  "totalAndCertificate",
  "negativeTotalAndNegativeCertificate",
  "totalAndSavings",
  "negativeTotalAndNegativeSavings",
  "totalAndIRA",
  "negativeTotalAndNegativeIRA",
  "totalAndSavingsAndCertificate",
  "totalAndNegativeSavingsAndCertificate",
  "negativeTotalAndNegativeSavingsAndCertificate",
  "totalAndSavingsAndNegativeCertificate",
  "negativeTotalAndNegativeSavingsAndNegativeCertificate",
  "totalAndCertificateAndIRA",
  "totalAndCertificateAndNegativeIRA",
  "negativeTotalAndCertificateAndNegativeIRA",
  "totalAndNegativeCertificateAndIRA",
  "negativeTotalAndNegativeCertificateAndIRA",
  "totalAndSavingsAndNegativeIRA",
  "negativeTotalAndSavingsAndNegativeIRA",
  "totalAndSavingsAndIRA",
  "totalAndNegativeSavingsAndIRA",
  "totalAndSavingsAndCertificateAndNegativeIRA",
  "negativeTotalAndSavingsAndCertificateAndNegativeIRA",
  "totalAndSavingsAndCertificateAndIRA",
  "totalAndNegativeSavingsAndCertificateAndIRA",
  "negativeTotalAndSavingsAndCertificateAndNegativeIRA",
  "totalAndSavingsAndNegativeCertificateAndIRA",
  "negativeTotalAndSavingsAndNegativeCertificateAndIRA",
  "totalAndSavingsAndNegativeCertificateAndNegativeIRA",
  "negativeTotalAndSavingsAndNegativeCertificateAndNegativeIRA",
  "totalAndSavingsAndNegativeCertificateAndNegativeIRA",
];

const parseSavings = (item: unknown): Savings => {
  const record = isRecord(item) ? item : {};
  const result: Savings = {};
  for (const key of SAVINGS_KEYS) {
    const value = parseOptionalTextContent(record[key as string]);
    if (value !== undefined) result[key] = value;
  }
  return result;
};

export const parseMonthlyOverview = (
  item: unknown,
  formLabel?: string,
): MonthlyOverview => {
  const parsed = parseWithRecord(item, monthlyOverviewFieldsSchema);
  const record = isRecord(item) ? item : {};
  return {
    type: ContentSectionType.MonthlyOverview,
    contentFrom: parsed.contentFrom,
    formLabel: formLabel ?? parsed.formLabel,
    moneyIn: parseMoneyIn(record[MONTHLY_OVERVIEW_RECORD_KEYS.MONEY_IN]),
    moneyOut: parseMoneyOut(record[MONTHLY_OVERVIEW_RECORD_KEYS.MONEY_OUT]),
    creditCard: isRecord(record[MONTHLY_OVERVIEW_RECORD_KEYS.CREDIT_CARD])
      ? parseCreditCard(record[MONTHLY_OVERVIEW_RECORD_KEYS.CREDIT_CARD])
      : undefined,
    savings: isRecord(record[MONTHLY_OVERVIEW_RECORD_KEYS.SAVINGS])
      ? parseSavings(record[MONTHLY_OVERVIEW_RECORD_KEYS.SAVINGS])
      : undefined,
  };
};
